"use client";

import { c as _c } from "react/compiler-runtime";
import { useT, useGameLocale } from "@/lib/i18n";
import { useState, useEffect, useRef, type MouseEvent as ReactMouseEvent, type CSSProperties } from "react";
import { useParams } from "next/navigation";
import { Link, useRouter } from "@/i18n/navigation";
import type { Monster, MonsterMove, MonsterMovePower, Power, AttackPattern } from "@/lib/api/types";
import type { EncounterStat } from "@/lib/encounter-stats";
import { randomPatternSentences, randomPatternSummary } from "@/lib/attack-pattern";
import { cachedFetch } from "@/lib/fetch-cache";
import { useBetaPrefix } from "@/lib/api/prefix.client";
import RichDescription from "@/app/components/RichDescription";
import LocalizedNames from "@/app/components/LocalizedNames";
import EntityUpdateHistory from "@/app/components/EntityUpdateHistory";
import EntityProse from "@/app/components/EntityProse";
import { imageUrl } from "@/lib/image-url";
import "@/app/card-revamp.css";
import "@/app/monster-encounter-extra.css";
import MonsterEncounterStats from "./MonsterEncounterStats";
import { SPINE_TEXT } from "@/lib/character-colors";
const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";

// Per-entity spine accent for the wiki page (--spine), keyed by monster type.
const SPINE_BY_TYPE: Record<string, string> = {
  Boss: "var(--color-ironclad)",
  Elite: "var(--accent-gold)",
  Normal: "var(--color-silent)"
};
const typeBadge: Record<string, string> = {
  Normal: "bg-surface text-fg-secondary",
  Elite: "bg-warning/10 text-warning",
  Boss: "bg-danger/10 text-danger"
};
const intentColors: Record<string, string> = {
  Attack: "text-danger",
  Defend: "text-info",
  Buff: "text-success",
  Debuff: "text-special",
  Status: "text-warning",
  Summon: "text-info",
  Heal: "text-success",
  Escape: "text-fg-muted",
  Sleep: "text-info",
  Stun: "text-warning",
  Special: "text-special",
  Unknown: "text-[var(--text-muted)]"
};
const intentIcons: Record<string, string> = {
  Attack: "⚔️",
  Defend: "🛡️",
  Buff: "⬆️",
  Debuff: "⬇️",
  Status: "📜",
  Summon: "👥",
  Heal: "💚",
  Escape: "💨",
  Sleep: "💤",
  Stun: "⚡",
  Special: "💀"
};
function PowerPill(t0) {
  const $ = _c(16);
  const {
    p,
    powerData,
    bp
  } = t0;
  const [showTooltip, setShowTooltip] = useState(false);
  const ref = useRef(null);
  const power = powerData[p.power_id];
  let t1;
  if ($[0] !== p.power_id || $[1] !== power) {
    t1 = power ? power.name : p.power_id.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, _temp);
    $[0] = p.power_id;
    $[1] = power;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const displayName = t1;
  let t2;
  if ($[3] !== p.power_id) {
    t2 = p.power_id.toLowerCase();
    $[3] = p.power_id;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = `${bp}/powers/${t2}`;
  const t4 = `relative text-xs px-2 py-0.5 rounded-full border transition-colors ${p.target === "player" ? "border-danger/30 bg-danger/10 text-danger hover:bg-danger/10" : "border-success/30 bg-success/10 text-success hover:bg-success/10"}`;
  let t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setShowTooltip(true);
    t6 = () => setShowTooltip(false);
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  let t7;
  if ($[7] !== power || $[8] !== showTooltip) {
    t7 = showTooltip && power && <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] shadow-xl pointer-events-none"><div className="flex items-center gap-2 mb-1.5">{power.image_url && <img src={imageUrl(power.image_url)} alt="" className="w-6 h-6 object-contain" crossOrigin="anonymous" />}<span className="font-semibold text-sm text-[var(--text-primary)]">{power.name}</span><span className={`text-[10px] px-1.5 py-0.5 rounded-full ml-auto ${power.type === "Debuff" ? "bg-danger/10 text-danger" : "bg-success/10 text-success"}`}>{power.type}</span></div><div className="text-xs text-[var(--text-secondary)] leading-relaxed"><RichDescription text={power.description} /></div><div className="absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 bg-[var(--bg-card)] border-r border-b border-[var(--border-subtle)] rotate-45 -mt-1" /></div>;
    $[7] = power;
    $[8] = showTooltip;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== displayName || $[11] !== p.amount || $[12] !== t3 || $[13] !== t4 || $[14] !== t7) {
    t8 = <Link ref={ref} href={t3} className={t4} onMouseEnter={t5} onMouseLeave={t6}>{displayName} {p.amount}{t7}</Link>;
    $[10] = displayName;
    $[11] = p.amount;
    $[12] = t3;
    $[13] = t4;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}

// Title-case a raw id ("EYE_LASERS" -> "Eye Lasers").
function _temp(c) {
  return c.toUpperCase();
}
function titleCaseId(id: string): string {
  return id.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

// A plain-English, one-line summary of what a move does, built from its
// structured fields (damage / block / heal / applied powers). English only —
// non-English monster pages skip it to avoid duplicating boilerplate across
// the localized variants (same policy as EntityProse). Falls back to an
// intent-derived line for moves that carry no numbers or powers.
function describeMove(move: MonsterMove, powerData: Record<string, Power>): string | null {
  const clauses: string[] = [];
  const d = move.damage;
  if (d && d.normal != null) {
    const multi = !!(d.hit_count && d.hit_count > 1);
    const base = multi ? `hits ${d.hit_count} times for ${d.normal} (${d.normal * d.hit_count!} total)` : `hits for ${d.normal}`;
    const asc = d.ascension != null && d.ascension !== d.normal ? ` (${multi ? `${d.ascension}×${d.hit_count} = ${d.ascension * d.hit_count!}` : d.ascension} on Ascension)` : "";
    clauses.push(`${base} damage${asc}`);
  }
  if (move.block != null) clauses.push(`gains ${move.block} Block`);
  if (move.heal != null) clauses.push(`heals ${move.heal} HP`);
  for (const p of move.powers || []) {
    const nm = powerData[p.power_id]?.name || titleCaseId(p.power_id);
    clauses.push(`${p.target === "player" ? "applies" : "gains"} ${p.amount} ${nm}`);
  }
  if (clauses.length === 0) {
    // No numbers or powers parsed — lean on the intent so the move still reads.
    const intent = (move.intent || "").toLowerCase();
    if (intent.includes("debuff")) return "Applies a debuff to you.";
    if (intent.includes("buff")) return "Strengthens itself with a buff.";
    if (intent.includes("defend")) return "Braces behind Block.";
    if (intent.includes("status")) return "Adds a status effect.";
    if (intent.includes("escape")) return "Prepares to flee the fight.";
    return null;
  }
  const joined = clauses.length === 1 ? clauses[0] : clauses.length === 2 ? `${clauses[0]} and ${clauses[1]}` : `${clauses.slice(0, -1).join(", ")}, and ${clauses[clauses.length - 1]}`;
  return joined.charAt(0).toUpperCase() + joined.slice(1) + ".";
}

// Ordered list of move display names for an attack pattern, so it can render
// as a visible sequence of chips. Follows the state machine's `next` links for
// a cycle; for other pattern types, lists the distinct moves it can pick.
function patternSteps(pattern: AttackPattern, moves: MonsterMove[]): string[] {
  const states = pattern.states || [];
  if (states.length === 0) return [];
  const nameOf = (mid: string) => moves.find(m => m.id === mid)?.name || titleCaseId(mid);
  if (pattern.type === "cycle") {
    const byId = new Map(states.map(s => [s.id, s]));
    const start = states.find(s => s.move_id === pattern.initial_move) || states.find(s => s.move_id) || states[0];
    const seen = new Set<string>();
    const out: string[] = [];
    let cur: typeof start | undefined = start;
    while (cur && !seen.has(cur.id)) {
      seen.add(cur.id);
      if (cur.move_id) out.push(nameOf(cur.move_id));
      cur = cur.next ? byId.get(cur.next) : undefined;
    }
    // Guard against a malformed chain that skipped most states.
    if (out.length >= 2) return out;
  }

  // random / conditional / mixed (or a broken cycle): unique moves it can use.
  const ids: string[] = [];
  for (const s of states) {
    if (s.move_id) ids.push(s.move_id);
    for (const b of s.branches || []) if (b.move_id) ids.push(b.move_id);
  }
  return [...new Set(ids)].map(nameOf);
}
function MoveCard(t0) {
  const $ = _c(49);
  const {
    move,
    powerData,
    bp,
    isEnglish
  } = t0;
  const t = useT();
  const t1 = move.intent || "Unknown";
  let effect;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== isEnglish || $[1] !== move || $[2] !== powerData || $[3] !== t1) {
    const intentParts = t1.split(" + ");
    let t7;
    if ($[10] !== isEnglish || $[11] !== move || $[12] !== powerData) {
      t7 = isEnglish ? describeMove(move, powerData) : null;
      $[10] = isEnglish;
      $[11] = move;
      $[12] = powerData;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    effect = t7;
    t6 = "move";
    t4 = "move-head";
    if ($[14] !== move.name) {
      t5 = <span className="move-name">{move.name}</span>;
      $[14] = move.name;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    t2 = "intents";
    t3 = intentParts.map(_temp2);
    $[0] = isEnglish;
    $[1] = move;
    $[2] = powerData;
    $[3] = t1;
    $[4] = effect;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    effect = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
  }
  let t7;
  if ($[16] !== t2 || $[17] !== t3) {
    t7 = <div className={t2}>{t3}</div>;
    $[16] = t2;
    $[17] = t3;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t4 || $[20] !== t5 || $[21] !== t7) {
    t8 = <div className={t4}>{t5}{t7}</div>;
    $[19] = t4;
    $[20] = t5;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== effect) {
    t9 = effect && <p className="move-effect">{effect}</p>;
    $[23] = effect;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== move.damage || $[26] !== t) {
    t10 = move.damage && <div className="mrow"><span className="mk">{t("Damage")}</span><span className="mval text-danger">{move.damage.normal}{move.damage.hit_count && move.damage.hit_count > 1 ? ` × ${move.damage.hit_count} = ${move.damage.normal * move.damage.hit_count}` : ""}</span>{move.damage.ascension != null && <span className="masc">(A: {move.damage.ascension}{move.damage.hit_count && move.damage.hit_count > 1 ? ` × ${move.damage.hit_count} = ${move.damage.ascension * move.damage.hit_count}` : ""})</span>}</div>;
    $[25] = move.damage;
    $[26] = t;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== move.block || $[29] !== t) {
    t11 = move.block != null && <div className="mrow"><span className="mk">{t("Block")}</span><span className="mval text-info">{move.block}</span></div>;
    $[28] = move.block;
    $[29] = t;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== move.heal || $[32] !== t) {
    t12 = move.heal != null && <div className="mrow"><span className="mk">{t("Heal")}</span><span className="mval text-success">{move.heal}</span></div>;
    $[31] = move.heal;
    $[32] = t;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== bp || $[35] !== move.powers || $[36] !== powerData || $[37] !== t) {
    t13 = move.powers && move.powers.length > 0 && <div className="mrow eff"><span className="mk">{t("Effects")}</span><div className="pills">{move.powers.map((p, i_0) => <PowerPill key={i_0} p={p} powerData={powerData} bp={bp} />)}</div></div>;
    $[34] = bp;
    $[35] = move.powers;
    $[36] = powerData;
    $[37] = t;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== t10 || $[40] !== t11 || $[41] !== t12 || $[42] !== t13) {
    t14 = <div className="mstats">{t10}{t11}{t12}{t13}</div>;
    $[39] = t10;
    $[40] = t11;
    $[41] = t12;
    $[42] = t13;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== t14 || $[45] !== t6 || $[46] !== t8 || $[47] !== t9) {
    t15 = <div className={t6}>{t8}{t9}{t14}</div>;
    $[44] = t14;
    $[45] = t6;
    $[46] = t8;
    $[47] = t9;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  return t15;
}
function _temp2(intent, i) {
  return <span key={i} className={`intent ${intentColors[intent] || intentColors.Unknown}`}>{intentIcons[intent] ? `${intentIcons[intent]} ` : ""}{intent}</span>;
}
const BUILD_CHAR_DOT: Record<string, string> = {
  IRONCLAD: "var(--color-ironclad)",
  SILENT: "var(--color-silent)",
  DEFECT: "var(--color-defect)",
  NECROBINDER: "var(--color-necrobinder)",
  REGENT: "var(--color-regent)"
};
interface EncounterBuild {
  character: string;
  name: string;
  size: number;
  win_rate: number;
  deaths: number;
  reached?: number | null;
  death_rate: number;
}
export default function MonsterDetail(t0) {
  const $ = _c(268);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    initialMonster,
    encounterStats
  } = t1;
  const {
    id
  } = useParams();
  const router = useRouter();
  const lang = useGameLocale();
  const t = useT();
  const bp = useBetaPrefix();
  const [monster, setMonster] = useState(initialMonster ?? null);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [powerData, setPowerData] = useState(t2);
  const [loading, setLoading] = useState(!initialMonster);
  const [notFound, setNotFound] = useState(false);
  const [betaArt, setBetaArt] = useState(false);
  const [activeSection, setActiveSection] = useState("stats");
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [builds, setBuilds] = useState(t3);
  let t4;
  if ($[4] !== lang || $[5] !== monster?.encounters) {
    t4 = () => {
      const encs = monster?.encounters?.map(_temp3) ?? [];
      if (encs.length === 0) {
        setBuilds([]);
        return;
      }
      let dead = false;
      Promise.all(encs.map(eid => cachedFetch(`${API}/api/runs/encounter-builds?encounter=${eid}&lang=${lang}`).catch(_temp4))).then(results => {
        if (dead) {
          return;
        }
        const merged = new Map();
        for (const r of results) {
          if (!r?.available) {
            continue;
          }
          for (const b of r.builds) {
            const k = `${b.character}:${b.name}`;
            const prev = merged.get(k);
            if (prev) {
              prev.deaths = prev.deaths + b.deaths;
            } else {
              merged.set(k, {
                ...b
              });
            }
          }
        }
        const rows = [...merged.values()].map(_temp5);
        rows.sort(_temp6);
        setBuilds(rows);
      });
      return () => {
        dead = true;
      };
    };
    $[4] = lang;
    $[5] = monster?.encounters;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== lang || $[8] !== monster) {
    t5 = [monster, lang];
    $[7] = lang;
    $[8] = monster;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  useEffect(t4, t5);
  let t6;
  if ($[10] !== id || $[11] !== initialMonster || $[12] !== lang) {
    t6 = () => {
      if (!id) {
        return;
      }
      cachedFetch(`${API}/api/monsters/${id}?lang=${lang}`).then(data => setMonster(data)).catch(() => {
        if (!initialMonster) {
          setNotFound(true);
        }
      }).finally(() => setLoading(false));
    };
    $[10] = id;
    $[11] = initialMonster;
    $[12] = lang;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== id || $[15] !== lang) {
    t7 = [id, lang];
    $[14] = id;
    $[15] = lang;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[17] !== lang || $[18] !== monster) {
    t8 = () => {
      if (!monster) {
        return;
      }
      const powerIds = new Set();
      if (monster.moves) {
        for (const move of monster.moves) {
          if (move.powers) {
            for (const p of move.powers) {
              powerIds.add(p.power_id);
            }
          }
        }
      }
      if (monster.innate_powers) {
        for (const p_0 of monster.innate_powers) {
          powerIds.add(p_0.power_id);
        }
      }
      if (powerIds.size === 0) {
        return;
      }
      cachedFetch(`${API}/api/powers?lang=${lang}`).then(powers => {
        const map = {};
        for (const pw of powers) {
          if (powerIds.has(pw.id)) {
            map[pw.id] = pw;
          }
        }
        setPowerData(map);
      });
    };
    t9 = [monster, lang];
    $[17] = lang;
    $[18] = monster;
    $[19] = t8;
    $[20] = t9;
  } else {
    t8 = $[19];
    t9 = $[20];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[21] !== monster) {
    t10 = () => {
      if (!monster) {
        return;
      }
      const secs = Array.from(document.querySelectorAll(".card-rvmp section[id]"));
      if (secs.length === 0) {
        return;
      }
      const obs = new IntersectionObserver(entries => {
        entries.forEach(e_0 => {
          if (e_0.isIntersecting) {
            setActiveSection((e_0.target as HTMLElement).id);
          }
        });
      }, {
        rootMargin: "-130px 0px -70% 0px"
      });
      secs.forEach(s => obs.observe(s));
      return () => obs.disconnect();
    };
    t11 = [monster];
    $[21] = monster;
    $[22] = t10;
    $[23] = t11;
  } else {
    t10 = $[22];
    t11 = $[23];
  }
  useEffect(t10, t11);
  let t12;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = (e_1, secId) => {
      e_1.preventDefault();
      const el = document.getElementById(secId);
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
        setActiveSection(secId);
      }
    };
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const handleTocClick = t12;
  if (loading) {
    let t13;
    if ($[25] !== t) {
      t13 = t("Loading...");
      $[25] = t;
      $[26] = t13;
    } else {
      t13 = $[26];
    }
    let t14;
    if ($[27] !== t13) {
      t14 = <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8"><div className="text-center py-12 text-[var(--text-muted)]">{t13}</div></div>;
      $[27] = t13;
      $[28] = t14;
    } else {
      t14 = $[28];
    }
    return t14;
  }
  if (notFound || !monster) {
    const t13 = `${bp}/monsters`;
    let t14;
    if ($[29] !== t) {
      t14 = t("Back to");
      $[29] = t;
      $[30] = t14;
    } else {
      t14 = $[30];
    }
    let t15;
    if ($[31] !== t) {
      t15 = t("Monsters");
      $[31] = t;
      $[32] = t15;
    } else {
      t15 = $[32];
    }
    let t16;
    if ($[33] !== t13 || $[34] !== t14 || $[35] !== t15) {
      t16 = <Link href={t13} className="text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors mb-6 inline-block">← {t14} {t15}</Link>;
      $[33] = t13;
      $[34] = t14;
      $[35] = t15;
      $[36] = t16;
    } else {
      t16 = $[36];
    }
    let t17;
    if ($[37] !== t) {
      t17 = t("Monster Not Found");
      $[37] = t;
      $[38] = t17;
    } else {
      t17 = $[38];
    }
    let t18;
    if ($[39] !== t17) {
      t18 = <h1 className="text-2xl font-bold text-[var(--text-primary)] mb-2">{t17}</h1>;
      $[39] = t17;
      $[40] = t18;
    } else {
      t18 = $[40];
    }
    let t19;
    if ($[41] !== id || $[42] !== t) {
      t19 = t("No monster with id \u201C{id}\u201D exists.", {
        id
      });
      $[41] = id;
      $[42] = t;
      $[43] = t19;
    } else {
      t19 = $[43];
    }
    let t20;
    if ($[44] !== t19) {
      t20 = <p className="text-[var(--text-muted)]">{t19}</p>;
      $[44] = t19;
      $[45] = t20;
    } else {
      t20 = $[45];
    }
    let t21;
    if ($[46] !== t18 || $[47] !== t20) {
      t21 = <div className="text-center py-12">{t18}{t20}</div>;
      $[46] = t18;
      $[47] = t20;
      $[48] = t21;
    } else {
      t21 = $[48];
    }
    let t22;
    if ($[49] !== t16 || $[50] !== t21) {
      t22 = <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{t16}{t21}</div>;
      $[49] = t16;
      $[50] = t21;
      $[51] = t22;
    } else {
      t22 = $[51];
    }
    return t22;
  }
  const isEnglish = lang === "eng";
  let t13;
  if ($[52] !== monster.encounters) {
    t13 = monster.encounters ? [...new Set(monster.encounters.filter(_temp7).map(_temp8))] : [];
    $[52] = monster.encounters;
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  const acts = t13;
  let t14;
  bb0: {
    if (!encounterStats?.length || !monster.encounters?.length) {
      t14 = null;
      break bb0;
    }
    let best = null;
    if ($[54] !== best || $[55] !== encounterStats || $[56] !== monster.encounters) {
      for (const enc of monster.encounters) {
        const s_0 = encounterStats.find(x_0 => x_0.encounter_id === enc.encounter_id);
        if (!s_0 || !s_0.total) {
          continue;
        }
        const killRate = s_0.fatal / s_0.total * 100;
        if (!best || killRate > best.killRate) {
          best = {
            name: enc.encounter_name,
            killRate
          };
        }
      }
      $[54] = best;
      $[55] = encounterStats;
      $[56] = monster.encounters;
      $[57] = best;
    } else {
      best = $[57];
    }
    t14 = best;
  }
  const deadliest = t14;
  const spineColor = SPINE_BY_TYPE[monster.type] ?? "var(--color-silent)";
  const heroSrc = betaArt && monster.beta_image_url ? monster.beta_image_url : monster.image_url;
  const hpNormal = monster.min_hp ? `${monster.min_hp}${monster.max_hp && monster.max_hp !== monster.min_hp ? `–${monster.max_hp}` : ""}` : null;
  const hpAscension = monster.min_hp_ascension ? `${monster.min_hp_ascension}${monster.max_hp_ascension && monster.max_hp_ascension !== monster.min_hp_ascension ? `–${monster.max_hp_ascension}` : ""}` : null;
  const hasStats = !!(hpNormal || hpAscension || monster.innate_powers && monster.innate_powers.length > 0 || monster.attack_pattern);
  const hasMoves = !!(monster.moves && monster.moves.length > 0);
  const hasQuotes = !!(monster.quotes && monster.quotes.length > 0);
  const hasEncounters = !!(monster.encounters && monster.encounters.length > 0);
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t26;
  if ($[58] !== activeSection || $[59] !== acts || $[60] !== bp || $[61] !== builds || $[62] !== deadliest || $[63] !== hasEncounters || $[64] !== hasMoves || $[65] !== hasQuotes || $[66] !== hasStats || $[67] !== heroSrc || $[68] !== hpAscension || $[69] !== hpNormal || $[70] !== isEnglish || $[71] !== lang || $[72] !== monster || $[73] !== powerData || $[74] !== router || $[75] !== spineColor || $[76] !== t) {
    const struggles = builds.filter(_temp9).slice(0, 5);
    const struggleKeys = new Set(struggles.map(_temp10));
    const handles = [...builds].sort(_temp11).filter(b_4 => (b_4.reached != null ? b_4.reached >= 50 : b_4.size >= 500) && !struggleKeys.has(`${b_4.character}:${b_4.name}`)).slice(0, 5);
    const hasBuilds = struggles.length > 0;
    let t27;
    if ($[89] !== hasStats || $[90] !== t) {
      t27 = hasStats ? [{
        id: "stats",
        label: t("Stats")
      }] : [];
      $[89] = hasStats;
      $[90] = t;
      $[91] = t27;
    } else {
      t27 = $[91];
    }
    let t28;
    if ($[92] !== hasMoves || $[93] !== t) {
      t28 = hasMoves ? [{
        id: "moves",
        label: t("Moves")
      }] : [];
      $[92] = hasMoves;
      $[93] = t;
      $[94] = t28;
    } else {
      t28 = $[94];
    }
    let t29;
    if ($[95] !== hasQuotes || $[96] !== t) {
      t29 = hasQuotes ? [{
        id: "quotes",
        label: t("Quotes")
      }] : [];
      $[95] = hasQuotes;
      $[96] = t;
      $[97] = t29;
    } else {
      t29 = $[97];
    }
    let t30;
    if ($[98] !== hasBuilds || $[99] !== t) {
      t30 = hasBuilds ? [{
        id: "builds",
        label: t("Builds")
      }] : [];
      $[98] = hasBuilds;
      $[99] = t;
      $[100] = t30;
    } else {
      t30 = $[100];
    }
    let t31;
    if ($[101] !== hasEncounters || $[102] !== t) {
      t31 = hasEncounters ? [{
        id: "encounters",
        label: t("Encounters")
      }] : [];
      $[101] = hasEncounters;
      $[102] = t;
      $[103] = t31;
    } else {
      t31 = $[103];
    }
    let t32;
    if ($[104] !== t) {
      t32 = t("Version history");
      $[104] = t;
      $[105] = t32;
    } else {
      t32 = $[105];
    }
    let t33;
    if ($[106] !== t32) {
      t33 = {
        id: "history",
        label: t32
      };
      $[106] = t32;
      $[107] = t33;
    } else {
      t33 = $[107];
    }
    let t34;
    if ($[108] !== t27 || $[109] !== t28 || $[110] !== t29 || $[111] !== t30 || $[112] !== t31 || $[113] !== t33) {
      t34 = [...t27, ...t28, ...t29, ...t30, ...t31, t33];
      $[108] = t27;
      $[109] = t28;
      $[110] = t29;
      $[111] = t30;
      $[112] = t31;
      $[113] = t33;
      $[114] = t34;
    } else {
      t34 = $[114];
    }
    const tocItems = t34;
    t24 = "card-rvmp";
    let t35;
    if ($[115] !== heroSrc) {
      t35 = heroSrc ? {
        "--entity-bg": `url("${imageUrl(heroSrc)}?bg")`
      } : {};
      $[115] = heroSrc;
      $[116] = t35;
    } else {
      t35 = $[116];
    }
    let t36;
    if ($[117] !== spineColor || $[118] !== t35) {
      t36 = {
        "--spine": spineColor,
        ...t35
      };
      $[117] = spineColor;
      $[118] = t35;
      $[119] = t36;
    } else {
      t36 = $[119];
    }
    t25 = t36 as CSSProperties;
    let t37;
    if ($[120] !== router) {
      t37 = () => router.back();
      $[120] = router;
      $[121] = t37;
    } else {
      t37 = $[121];
    }
    let t38;
    if ($[122] !== t) {
      t38 = t("Back to");
      $[122] = t;
      $[123] = t38;
    } else {
      t38 = $[123];
    }
    let t39;
    if ($[124] !== t) {
      t39 = t("Monsters");
      $[124] = t;
      $[125] = t39;
    } else {
      t39 = $[125];
    }
    if ($[126] !== t37 || $[127] !== t38 || $[128] !== t39) {
      t26 = <div className="cd-top"><button type="button" onClick={t37} className="cd-back">← {t38} {t39}</button></div>;
      $[126] = t37;
      $[127] = t38;
      $[128] = t39;
      $[129] = t26;
    } else {
      t26 = $[129];
    }
    t23 = "wrap";
    t15 = "main";
    let t40;
    if ($[130] === Symbol.for("react.memo_cache_sentinel")) {
      t40 = <span className="dot">◆</span>;
      $[130] = t40;
    } else {
      t40 = $[130];
    }
    let t41;
    if ($[131] !== acts) {
      t41 = acts.length > 0 && <><span>{acts.join(", ")}</span><span>·</span></>;
      $[131] = acts;
      $[132] = t41;
    } else {
      t41 = $[132];
    }
    let t42;
    if ($[133] !== monster.type) {
      t42 = <span>{monster.type}</span>;
      $[133] = monster.type;
      $[134] = t42;
    } else {
      t42 = $[134];
    }
    let t43;
    if ($[135] !== t41 || $[136] !== t42) {
      t43 = <p className="eyebrow">{t40}{t41}{t42}</p>;
      $[135] = t41;
      $[136] = t42;
      $[137] = t43;
    } else {
      t43 = $[137];
    }
    let t44;
    if ($[138] !== monster.name) {
      t44 = <h1>{monster.name}</h1>;
      $[138] = monster.name;
      $[139] = t44;
    } else {
      t44 = $[139];
    }
    if ($[140] !== t43 || $[141] !== t44) {
      t16 = <div className="hero">{t43}{t44}</div>;
      $[140] = t43;
      $[141] = t44;
      $[142] = t16;
    } else {
      t16 = $[142];
    }
    if ($[143] !== deadliest || $[144] !== monster) {
      t17 = <EntityProse kind="monster" monster={monster} deadliest={deadliest} />;
      $[143] = deadliest;
      $[144] = monster;
      $[145] = t17;
    } else {
      t17 = $[145];
    }
    let t45;
    if ($[146] !== t) {
      t45 = t("On this page");
      $[146] = t;
      $[147] = t45;
    } else {
      t45 = $[147];
    }
    let t46;
    if ($[148] !== t) {
      t46 = t("On this page");
      $[148] = t;
      $[149] = t46;
    } else {
      t46 = $[149];
    }
    let t47;
    if ($[150] !== t46) {
      t47 = <span className="toc-label">{t46}</span>;
      $[150] = t46;
      $[151] = t47;
    } else {
      t47 = $[151];
    }
    let t48;
    if ($[152] !== activeSection || $[153] !== tocItems) {
      t48 = tocItems.map(it => <a key={it.id} href={`#${it.id}`} className={activeSection === it.id ? "on" : undefined} onClick={e_4 => handleTocClick(e_4, it.id)}>{it.label}</a>);
      $[152] = activeSection;
      $[153] = tocItems;
      $[154] = t48;
    } else {
      t48 = $[154];
    }
    if ($[155] !== t45 || $[156] !== t47 || $[157] !== t48) {
      t18 = <nav className="toc" aria-label={t45}>{t47}{t48}</nav>;
      $[155] = t45;
      $[156] = t47;
      $[157] = t48;
      $[158] = t18;
    } else {
      t18 = $[158];
    }
    if ($[159] !== bp || $[160] !== hasMoves || $[161] !== hasStats || $[162] !== hpAscension || $[163] !== hpNormal || $[164] !== lang || $[165] !== monster.attack_pattern || $[166] !== monster.innate_powers || $[167] !== monster.moves || $[168] !== monster.type || $[169] !== powerData || $[170] !== t) {
      t19 = hasStats && <section id="stats"><h2>{t("Stats")}</h2>{(hpNormal || hpAscension || hasMoves) && <div className="tiles">{hpNormal && <div className="tile"><div className="k">{t("Hit Points")}</div><div className="v" style={{
              color: "var(--warn)"
            }}>{hpNormal}</div></div>}{hpAscension && <div className="tile"><div className="k">{t("HP \xB7 Ascension")}</div><div className="v" style={{
              color: "var(--warn)"
            }}>{hpAscension}</div></div>}{hasMoves && <div className="tile"><div className="k">{t("Moves")}</div><div className="v">{monster.moves.length}</div></div>}<div className="tile"><div className="k">{t("Type")}</div><div className="v" style={{
              color: SPINE_TEXT,
              fontSize: 20
            }}>{monster.type}</div></div></div>}{monster.innate_powers && monster.innate_powers.length > 0 && <><h3 className="subh">{t("Innate Powers")}</h3><p className="h-note">{t("Applied at the start of combat")}</p><div className="pills">{monster.innate_powers.map((p_1, i) => <span key={i} className="flex items-center gap-1"><PowerPill p={{
                power_id: p_1.power_id,
                target: "self",
                amount: p_1.amount
              }} powerData={powerData} bp={bp} />{p_1.amount_ascension != null && p_1.amount_ascension !== p_1.amount && <span className="text-xs text-warning">(A: {p_1.amount_ascension})</span>}</span>)}</div></>}{monster.attack_pattern && (() => {
          const steps = patternSteps(monster.attack_pattern, monster.moves || []);
          const desc = monster.attack_pattern.description;
          const isCycle = monster.attack_pattern.type === "cycle";
          const summary = isCycle ? null : randomPatternSummary(monster.attack_pattern);
          const moveName = mid => (monster.moves || []).find(m => m.id === mid)?.name || titleCaseId(mid);
          if (summary) {
            const sentences = randomPatternSentences(summary, moveName, t, lang);
            return <><h3 className="subh">{t("Attack Pattern")}</h3><p className="desc-body">{sentences.join(" ")}</p><div className="atk-seq">{steps.map(_temp12)}</div></>;
          }
          return <><h3 className="subh">{t("Attack Pattern")}</h3>{steps.length > 1 ? <div className="atk-seq">{steps.map((s_2, i_1) => <span key={i_1} className="atk-step-wrap"><span className="atk-step">{s_2}</span>{i_1 < steps.length - 1 && <span className="atk-arrow">→</span>}</span>)}{isCycle && <span className="atk-repeat" title={t("Repeats")}>↻</span>}</div> : desc && <p className="desc-body">{desc}</p>}</>;
        })()}</section>;
      $[159] = bp;
      $[160] = hasMoves;
      $[161] = hasStats;
      $[162] = hpAscension;
      $[163] = hpNormal;
      $[164] = lang;
      $[165] = monster.attack_pattern;
      $[166] = monster.innate_powers;
      $[167] = monster.moves;
      $[168] = monster.type;
      $[169] = powerData;
      $[170] = t;
      $[171] = t19;
    } else {
      t19 = $[171];
    }
    if ($[172] !== bp || $[173] !== hasMoves || $[174] !== isEnglish || $[175] !== monster.moves || $[176] !== powerData || $[177] !== t) {
      t20 = hasMoves && <section id="moves"><h2>{t("Moves")} ({monster.moves.length})</h2><div className="moves">{monster.moves.map(move_0 => <MoveCard key={move_0.id} move={move_0} powerData={powerData} bp={bp} isEnglish={isEnglish} />)}</div></section>;
      $[172] = bp;
      $[173] = hasMoves;
      $[174] = isEnglish;
      $[175] = monster.moves;
      $[176] = powerData;
      $[177] = t;
      $[178] = t20;
    } else {
      t20 = $[178];
    }
    if ($[179] !== hasQuotes || $[180] !== monster.name || $[181] !== monster.quotes || $[182] !== t) {
      t21 = hasQuotes && <section id="quotes"><h2>{t("Quotes")}</h2><p className="h-note">{t("What {name} says in combat.", {
            name: monster.name
          })}</p><ul className="space-y-3">{monster.quotes.map(_temp13)}</ul></section>;
      $[179] = hasQuotes;
      $[180] = monster.name;
      $[181] = monster.quotes;
      $[182] = t;
      $[183] = t21;
    } else {
      t21 = $[183];
    }
    t22 = hasBuilds && <section id="builds"><h2>{t("Builds")}</h2><p className="h-note">{t("Of each community build's runs that reach {name}, the share that die to it, next to that build's overall win rate.", {
          name: monster.name
        })}</p><div style={{
        display: "grid",
        gap: "1rem",
        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))"
      }}><div><p className="h-note" style={{
            color: "var(--warn, #ef4444)"
          }}>{t("Dies here most")}</p>{struggles.map(b_5 => <div key={`s-${b_5.character}-${b_5.name}`} style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            padding: "0.25rem 0",
            fontSize: "0.85rem"
          }}><span style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: BUILD_CHAR_DOT[b_5.character] || "#888",
              flexShrink: 0
            }} /><span className="cn" style={{
              flexGrow: 1
            }}>{b_5.name}</span><span style={{
              color: "var(--text-muted)",
              fontVariantNumeric: "tabular-nums"
            }}>{b_5.death_rate}% {t("die here")} ·{" "}{t("{pct}% WR", {
                pct: b_5.win_rate
              })}</span></div>)}</div>{handles.length > 0 && <div><p className="h-note" style={{
            color: "var(--good, #22c55e)"
          }}>{t("Handles it best")}</p>{handles.map(b_6 => <div key={`h-${b_6.character}-${b_6.name}`} style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            padding: "0.25rem 0",
            fontSize: "0.85rem"
          }}><span style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: BUILD_CHAR_DOT[b_6.character] || "#888",
              flexShrink: 0
            }} /><span className="cn" style={{
              flexGrow: 1
            }}>{b_6.name}</span><span style={{
              color: "var(--text-muted)",
              fontVariantNumeric: "tabular-nums"
            }}>{t("{pct}% WR", {
                pct: b_6.win_rate
              })} · {b_6.death_rate}% {t("die here")}</span></div>)}</div>}</div></section>;
    $[58] = activeSection;
    $[59] = acts;
    $[60] = bp;
    $[61] = builds;
    $[62] = deadliest;
    $[63] = hasEncounters;
    $[64] = hasMoves;
    $[65] = hasQuotes;
    $[66] = hasStats;
    $[67] = heroSrc;
    $[68] = hpAscension;
    $[69] = hpNormal;
    $[70] = isEnglish;
    $[71] = lang;
    $[72] = monster;
    $[73] = powerData;
    $[74] = router;
    $[75] = spineColor;
    $[76] = t;
    $[77] = t15;
    $[78] = t16;
    $[79] = t17;
    $[80] = t18;
    $[81] = t19;
    $[82] = t20;
    $[83] = t21;
    $[84] = t22;
    $[85] = t23;
    $[86] = t24;
    $[87] = t25;
    $[88] = t26;
  } else {
    t15 = $[77];
    t16 = $[78];
    t17 = $[79];
    t18 = $[80];
    t19 = $[81];
    t20 = $[82];
    t21 = $[83];
    t22 = $[84];
    t23 = $[85];
    t24 = $[86];
    t25 = $[87];
    t26 = $[88];
  }
  let t27;
  if ($[184] !== bp || $[185] !== encounterStats || $[186] !== hasEncounters || $[187] !== monster.encounters || $[188] !== monster.name || $[189] !== t) {
    t27 = hasEncounters && <section id="encounters"><h2>{t("Encounters")}</h2><p className="h-note">{t("Where {name} shows up.", {
          name: monster.name
        })}</p>{encounterStats && encounterStats.length > 0 && <div className="enc-deadliness">{monster.encounters.map(enc_0 => {
          const s_3 = encounterStats.find(x_1 => x_1.encounter_id === enc_0.encounter_id);
          if (!s_3 || !s_3.total) {
            return null;
          }
          const killRate_0 = s_3.fatal / s_3.total * 100;
          return <p key={enc_0.encounter_id} className="stat-note">{t("In {total} community runs, the {name} fight was fatal to {pct}% of runs ({fatal}), dealing an average of {dmg} damage over {turns} turns.", {
              total: s_3.total.toLocaleString(),
              name: enc_0.encounter_name,
              pct: killRate_0.toFixed(1),
              fatal: s_3.fatal.toLocaleString(),
              dmg: s_3.avg_damage,
              turns: s_3.avg_turns
            })}</p>;
        })}</div>}<MonsterEncounterStats encounters={monster.encounters.map(_temp14)} bp={bp} /><div className="enc-list mt-4">{monster.encounters.map(enc_1 => <Link key={enc_1.encounter_id} href={`${bp}/encounters/${enc_1.encounter_id.toLowerCase()}`} className="enc-row"><span className="enc-name">{enc_1.encounter_name}</span><div className="enc-meta">{enc_1.act && <span className="badge">{enc_1.act}</span>}<span className={`badge ${typeBadge[enc_1.room_type] || ""}`}>{enc_1.room_type}</span>{enc_1.is_weak && <span className="badge bg-success/10 text-success">{t("Weak")}</span>}</div></Link>)}</div></section>;
    $[184] = bp;
    $[185] = encounterStats;
    $[186] = hasEncounters;
    $[187] = monster.encounters;
    $[188] = monster.name;
    $[189] = t;
    $[190] = t27;
  } else {
    t27 = $[190];
  }
  let t28;
  let t29;
  if ($[191] !== id) {
    t28 = <LocalizedNames entityType="monsters" entityId={id} />;
    t29 = <EntityUpdateHistory entityType="monsters" entityId={id} />;
    $[191] = id;
    $[192] = t28;
    $[193] = t29;
  } else {
    t28 = $[192];
    t29 = $[193];
  }
  let t30;
  if ($[194] !== t15 || $[195] !== t16 || $[196] !== t17 || $[197] !== t18 || $[198] !== t19 || $[199] !== t20 || $[200] !== t21 || $[201] !== t22 || $[202] !== t27 || $[203] !== t28 || $[204] !== t29) {
    t30 = <main className={t15}>{t16}{t17}{t18}{t19}{t20}{t21}{t22}{t27}{t28}{t29}</main>;
    $[194] = t15;
    $[195] = t16;
    $[196] = t17;
    $[197] = t18;
    $[198] = t19;
    $[199] = t20;
    $[200] = t21;
    $[201] = t22;
    $[202] = t27;
    $[203] = t28;
    $[204] = t29;
    $[205] = t30;
  } else {
    t30 = $[205];
  }
  let t31;
  if ($[206] !== heroSrc || $[207] !== monster.name || $[208] !== t) {
    t31 = heroSrc && <img className="sprite" src={imageUrl(heroSrc)} alt={t("{name} - Slay the Spire 2 Monster", {
      name: monster.name
    })} crossOrigin="anonymous" />;
    $[206] = heroSrc;
    $[207] = monster.name;
    $[208] = t;
    $[209] = t31;
  } else {
    t31 = $[209];
  }
  let t32;
  if ($[210] !== betaArt || $[211] !== monster.beta_image_url || $[212] !== t) {
    t32 = monster.beta_image_url && <div className="variant"><button type="button" className={`betabtn${betaArt ? " on" : ""}`} aria-pressed={betaArt} onClick={() => setBetaArt(!betaArt)} title={betaArt ? t("Show current art") : t("Show concept art")}>{betaArt ? t("Current art") : t("Concept art")}</button></div>;
    $[210] = betaArt;
    $[211] = monster.beta_image_url;
    $[212] = t;
    $[213] = t32;
  } else {
    t32 = $[213];
  }
  let t33;
  if ($[214] !== t) {
    t33 = t("At a glance");
    $[214] = t;
    $[215] = t33;
  } else {
    t33 = $[215];
  }
  let t34;
  if ($[216] !== t33) {
    t34 = <div className="fh">{t33}</div>;
    $[216] = t33;
    $[217] = t34;
  } else {
    t34 = $[217];
  }
  let t35;
  if ($[218] !== hpNormal || $[219] !== t) {
    t35 = hpNormal && <div className="frow"><dt>{t("Hit Points")}</dt><dd>{hpNormal}</dd></div>;
    $[218] = hpNormal;
    $[219] = t;
    $[220] = t35;
  } else {
    t35 = $[220];
  }
  let t36;
  if ($[221] !== hpAscension || $[222] !== t) {
    t36 = hpAscension && <div className="frow"><dt>{t("HP \xB7 Ascension")}</dt><dd style={{
        color: "var(--warn)"
      }}>{hpAscension}</dd></div>;
    $[221] = hpAscension;
    $[222] = t;
    $[223] = t36;
  } else {
    t36 = $[223];
  }
  let t37;
  if ($[224] !== t) {
    t37 = t("Type");
    $[224] = t;
    $[225] = t37;
  } else {
    t37 = $[225];
  }
  let t38;
  if ($[226] !== t37) {
    t38 = <dt>{t37}</dt>;
    $[226] = t37;
    $[227] = t38;
  } else {
    t38 = $[227];
  }
  let t39;
  if ($[228] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = {
      color: SPINE_TEXT
    };
    $[228] = t39;
  } else {
    t39 = $[228];
  }
  let t40;
  if ($[229] !== monster.type) {
    t40 = <dd style={t39}>{monster.type}</dd>;
    $[229] = monster.type;
    $[230] = t40;
  } else {
    t40 = $[230];
  }
  let t41;
  if ($[231] !== t38 || $[232] !== t40) {
    t41 = <div className="frow">{t38}{t40}</div>;
    $[231] = t38;
    $[232] = t40;
    $[233] = t41;
  } else {
    t41 = $[233];
  }
  let t42;
  if ($[234] !== acts || $[235] !== t) {
    t42 = acts.length > 0 && <div className="frow"><dt>{t("Act")}</dt><dd>{acts.join(", ")}</dd></div>;
    $[234] = acts;
    $[235] = t;
    $[236] = t42;
  } else {
    t42 = $[236];
  }
  let t43;
  if ($[237] !== hasMoves || $[238] !== monster.moves || $[239] !== t) {
    t43 = hasMoves && <div className="frow"><dt>{t("Moves")}</dt><dd>{monster.moves.length}</dd></div>;
    $[237] = hasMoves;
    $[238] = monster.moves;
    $[239] = t;
    $[240] = t43;
  } else {
    t43 = $[240];
  }
  let t44;
  if ($[241] !== hasEncounters || $[242] !== monster.encounters || $[243] !== t) {
    t44 = hasEncounters && <div className="frow"><dt>{t("Encounters")}</dt><dd>{monster.encounters.length}</dd></div>;
    $[241] = hasEncounters;
    $[242] = monster.encounters;
    $[243] = t;
    $[244] = t44;
  } else {
    t44 = $[244];
  }
  let t45;
  if ($[245] !== t35 || $[246] !== t36 || $[247] !== t41 || $[248] !== t42 || $[249] !== t43 || $[250] !== t44) {
    t45 = <dl>{t35}{t36}{t41}{t42}{t43}{t44}</dl>;
    $[245] = t35;
    $[246] = t36;
    $[247] = t41;
    $[248] = t42;
    $[249] = t43;
    $[250] = t44;
    $[251] = t45;
  } else {
    t45 = $[251];
  }
  let t46;
  if ($[252] !== t34 || $[253] !== t45) {
    t46 = <div className="facts">{t34}{t45}</div>;
    $[252] = t34;
    $[253] = t45;
    $[254] = t46;
  } else {
    t46 = $[254];
  }
  let t47;
  if ($[255] !== t31 || $[256] !== t32 || $[257] !== t46) {
    t47 = <aside className="aside"><div className="box">{t31}{t32}{t46}</div></aside>;
    $[255] = t31;
    $[256] = t32;
    $[257] = t46;
    $[258] = t47;
  } else {
    t47 = $[258];
  }
  let t48;
  if ($[259] !== t23 || $[260] !== t30 || $[261] !== t47) {
    t48 = <div className={t23}>{t30}{t47}</div>;
    $[259] = t23;
    $[260] = t30;
    $[261] = t47;
    $[262] = t48;
  } else {
    t48 = $[262];
  }
  let t49;
  if ($[263] !== t24 || $[264] !== t25 || $[265] !== t26 || $[266] !== t48) {
    t49 = <div className={t24} style={t25}>{t26}{t48}</div>;
    $[263] = t24;
    $[264] = t25;
    $[265] = t26;
    $[266] = t48;
    $[267] = t49;
  } else {
    t49 = $[267];
  }
  return t49;
}
function _temp14(e_5) {
  return {
    encounter_id: e_5.encounter_id,
    encounter_name: e_5.encounter_name
  };
}
function _temp13(q, i_2) {
  return <li key={`${q.key}-${i_2}`} className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] px-4 py-3"><blockquote className="text-[var(--text-primary)] italic">“{q.text}”</blockquote>{q.move && <div className="mt-1 text-xs uppercase tracking-wider text-[var(--text-muted)]">{q.move}</div>}</li>;
}
function _temp12(s_1, i_0) {
  return <span key={i_0} className="atk-step-wrap"><span className="atk-step">{s_1}</span></span>;
}
function _temp11(a, b_3) {
  return b_3.win_rate - a.win_rate;
}
function _temp10(b_2) {
  return `${b_2.character}:${b_2.name}`;
}
function _temp9(b_1) {
  return b_1.deaths >= 5;
}
function _temp8(e_3) {
  return e_3.act;
}
function _temp7(e_2) {
  return e_2.act;
}
function _temp6(x, y) {
  return y.death_rate - x.death_rate;
}
function _temp5(b_0) {
  return {
    ...b_0,
    death_rate: b_0.size ? Math.round(b_0.deaths / b_0.size * 10000) / 100 : 0
  };
}
function _temp4() {
  return null;
}
function _temp3(e) {
  return e.encounter_id;
}